import { Divider } from "@evergis/ui";
import React, { FC, memo, useMemo } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import { getLayerCopyrights } from "ducks/layerCopyrights/selectors";
import { LayerCopyrights } from "components/Dialogs/LayerCopyrights/LayerCopyrights";
import { StyledCopyrightContainer, StyledCopyrightLink as A, StyledCopyrightsContainer } from "./styled";
import { useToggle } from "../../hooks/useToggle";
import { useGetCopyrights } from "../../hooks/useGetCopyrights";

const MAX_FOOTER_COPYRIGHTS = 2;

export const Copyright: FC<{ basemap: string }> = memo(({ basemap }) => {
  const { t } = useTranslation("common");
  const copyrights = useGetCopyrights();
  const layerCopyrights = useSelector(getLayerCopyrights);

  const renderLayerCopyrights = useMemo(
    () => (
      <>
        {layerCopyrights.slice(0, MAX_FOOTER_COPYRIGHTS).map(layerCopyright => (
          <StyledCopyrightContainer key={layerCopyright}>{layerCopyright}</StyledCopyrightContainer>
        ))}
      </>
    ),
    [layerCopyrights],
  );

  const renderBaseCopyright = useMemo(() => {
    const copyright = copyrights.find(copy => copy.names.includes(basemap));

    if (!copyright) {
      return null;
    }

    const { isDark, CopyrightComponent } = copyright;

    return (
      <StyledCopyrightContainer
        color={isDark && "rgba(255, 255, 255, 0.7)"}
        backgroundColor={isDark && "rgba(0, 0, 0, 0.36)"}
      >
        {CopyrightComponent}
        <Divider
          style={{
            marginLeft: "0.25rem",
          }}
          vertical
        />
      </StyledCopyrightContainer>
    );
  }, [basemap, copyrights]);

  const [isOpenDialog, toggleOpenDialog] = useToggle();
  const renderMoreCopyrights = useMemo(
    () =>
      layerCopyrights.length <= MAX_FOOTER_COPYRIGHTS ? null : (
        <>
          <A onClick={toggleOpenDialog}>{t("copyright.more")}&hellip;</A>
          <LayerCopyrights basemap={basemap} isOpen={isOpenDialog} toggleOpen={toggleOpenDialog} />
        </>
      ),
    [layerCopyrights.length, basemap, isOpenDialog, toggleOpenDialog, t],
  );

  return (
    <StyledCopyrightsContainer>
      {renderLayerCopyrights}
      {renderBaseCopyright}
      {renderMoreCopyrights}
    </StyledCopyrightsContainer>
  );
});
